import {
  Component,
  PLATFORM_ID,
  computed,
  inject,
  input,
  linkedSignal,
  signal,
  viewChild,
  OnInit,
} from '@angular/core';
import { NodeSercon, Teaser } from '@app/models/entity.model';
import { ServiceInfoboxComponent } from '@app/components/service-infobox/service-infobox.component';
import { ParagraphLoaderComponent } from '@app/entities/paragraph/loader.component';

import { Icon } from '@app/models/global.model';
import {
  ParagraphPgMscs,
  ParagraphPgPnav,
  ParagraphPgScs,
} from '@app/models/paragraph.model';
import { TextsPipe } from '@app/pipes/texts.pipe';
import {
  MobiUiSplitButtonComponent,
  MobiUiSplitButtonGroupComponent,
} from '@mobi/rwc-ui-components-ng-jslib';
import { HeroTextComponent } from '@app/components/hero-text/hero-text.component';
import { BreadcrumbComponent } from '@app/components/breadcrumb/breadcrumb.component';
import { ContentSectionComponent } from '@app/components/content-section/content-section.component';
import { ConciergeComponent } from '@app/components/concierge/concierge.component';
import { ContentLinksComponent } from '@app/components/content-links/content-links.component';
import { TextsService } from '@app/services/texts.service';
import { TeaserListComponent } from '@app/components/teaser-list/teaser-list.component';
import { SectionTitleComponent } from '@app/components/section-title/section-title.component';
import { IllustrationComponent } from '@app/components/illustration/illustration.component';
import { IconCtaComponent } from '@app/components/icon-cta/icon-cta.component';
import { GaSearchComponent } from '@app/components/ga-search/ga-search.component';
import { EntitySharedModule } from '@app/modules/entity.shared.module';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { setTimeout$ } from '@app/helpers/timeout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type ServiceSectionKey =
  | 'damageReportServices'
  | 'administrativeServices'
  | 'mobilityServices'
  | 'invoiceServices'
  | 'customerBenefitsServices';

/** The structured service sections in page order: entity field, text keys for label and anchor, illustration */
const SERVICE_SECTIONS: ReadonlyArray<{
  key: ServiceSectionKey;
  nameKey: string;
  nameDefault: string;
  idKey: string;
  idDefault: string;
  illustration?: string;
}> = [
  {
    key: 'damageReportServices',
    nameKey: 'damage_report_services_name',
    nameDefault: 'Schaden',
    idKey: 'damage_report_services_paramenter_name',
    idDefault: 'schaden',
    illustration: 'basic_people_research_tablet',
  },
  {
    key: 'administrativeServices',
    nameKey: 'administrative_services_name',
    nameDefault: 'Administratives',
    idKey: 'administrative_services_parameter_name',
    idDefault: 'administratives',
    illustration: 'basic_family_house_outside',
  },
  {
    key: 'mobilityServices',
    nameKey: 'mobility_services_name',
    nameDefault: 'Mobilität',
    idKey: 'mobility_services_paramenter_name',
    idDefault: 'mobilitaet',
    illustration: 'other_car_check',
  },
  {
    key: 'invoiceServices',
    nameKey: 'invoice_services_name',
    nameDefault: 'Rechnungen',
    idKey: 'invoice_services_paramenter_name',
    idDefault: 'rechnungen',
  },
  {
    key: 'customerBenefitsServices',
    nameKey: 'customer_benefits_services_name',
    nameDefault: 'Kundenvorteile',
    idKey: 'customer_benefits_services_paramenter_name',
    idDefault: 'kundenvorteile',
    illustration: 'other_gondola_mountain',
  },
];

@Component({
  selector: 'mobi-node-sercon',
  templateUrl: './node-sercon.component.html',
  styleUrls: ['./node-sercon.component.scss'],
  standalone: true,
  imports: [
    EntitySharedModule,
    ServiceInfoboxComponent,
    TextsPipe,
    MobiUiSplitButtonGroupComponent,
    MobiUiSplitButtonComponent,
    HeroTextComponent,
    BreadcrumbComponent,
    ContentSectionComponent,
    ConciergeComponent,
    ContentLinksComponent,
    TeaserListComponent,
    SectionTitleComponent,
    GaSearchComponent,
    IllustrationComponent,
    IconCtaComponent,
    ParagraphLoaderComponent,
  ],
})
export class NodeSerconComponent implements OnInit {
  readonly entity = input.required<NodeSercon>();
  readonly params = input<URLSearchParams>(new URLSearchParams());
  visiblePage = linkedSignal<string>(() =>
    this.params().get('page') === 'contact' ? 'contact' : 'services'
  );
  hasSwitchedPage = signal(false);
  contentLinks = signal<ParagraphPgPnav[]>([]);

  readonly splitButtonGroup =
    viewChild<MobiUiSplitButtonGroupComponent>('splitButtonGroup');

  emergencyCtaIcon: Icon = {
    type: 'TaxonomyTermIcons',
    label: 'message_sos',
    symbol: 'message_sos',
  };
  #texts = inject(TextsService);
  #document = inject(DOCUMENT);
  #platformId = inject(PLATFORM_ID);

  async ngOnInit() {
    await this.getContentLinks();
  }

  mainTeasers = computed<Teaser[]>(() => {
    const mainServices = this.entity()?.mainServices;

    if (!mainServices || mainServices.length === 0) {
      return [];
    }

    return mainServices.map((teaser: ParagraphPgMscs) => ({
      teaserTitle: teaser.title,
      teaserTextShort: teaser.cta.title,
      url: teaser.cta,
      teaserIllustration: teaser?.illustration,
      created: '',
      bundle: 'ParagraphPgMscs',
      published: true,
      uuid: teaser.uuid,
    }));
  });

  serviceSections = computed(() =>
    SERVICE_SECTIONS.map((section) => ({
      ...section,
      services: this.entity()[section.key] as ReadonlyArray<ParagraphPgScs>,
      superTitle: this.entity()[`${section.key}SuperTitle`],
      title: this.entity()[`${section.key}Title`],
      illustration: section.illustration
        ? {
            name: section.illustration,
            file: { entity: { filename: `${section.illustration}.svg` } },
          }
        : undefined,
    }))
  );

  async getContentLinks() {
    // one content link per structured section that has a title
    for (const section of SERVICE_SECTIONS) {
      if (!this.entity()[`${section.key}Title`]) continue;
      await Promise.all([
        this.#texts.getText(section.nameKey, 'frontend', section.nameDefault),
        this.#texts.getText(section.idKey, 'frontend', section.idDefault),
      ])
        .then(([label, sectionId]) => {
          const link: ParagraphPgPnav = { label, sectionId };
          this.contentLinks.update((values) => [...values, link]);
        })
        .catch((e) => this.#handleError(e));
    }
  }

  changePage(page: string) {
    this.hasSwitchedPage.set(true);
    this.visiblePage.set(page);
    this.updatePageParam(page);
    setTimeout$(() => {
      this.#document.getElementById('scroll-to-view')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 100);
  }

  #handleError(e: any) {
    console.error(e);
    this.#document?.dispatchEvent(
      new CustomEvent('tracking:system', {
        detail: {
          message:
            e.error?.message ||
            e.message ||
            `An unknown error occurred: ${JSON.stringify(e)}`,
          type: 'error',
        },
      })
    );
  }

  updatePageParam(page: string) {
    if (!isPlatformBrowser(this.#platformId)) return;

    const url = new URL(this.#document.location.href);
    if (page === 'contact') {
      url.searchParams.set('page', page);
    } else {
      url.searchParams.delete('page');
    }
    window.history.replaceState(window.history.state, '', url.toString());
  }
}
